// Lib
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Components
import ElementTool from './ElementTool';
import RequireElementTypeEditPermission from './RequireElementTypeEditPermission';

// Selectors
import currentBoardSuggestedColorsSelector from '../selectionTools/colorTools/currentBoardSuggestedColorsSelector';

// Utils
import { parseColorObject } from '../../../../../common/colors/colorObjectUtil';
import { getUserPreferredColorDisplay, isFeatureEnabled } from '../../../../../common/users/utils/userPropertyUtils';
import { getColorName } from '../../../../../common/colors/colorNameUtil';
import rawFromText from '../../../../../common/utils/editor/rawUtils/rawFromText';
import convertSimpleStringToTiptapContent from '../../../../../common/tiptap/utils/createJsonContentUtils/convertSimpleStringToTiptapContent';

// Constants
import { COLOR_SWATCH_DISPLAY_NAME } from '../../../../../common/colorSwatches/colorSwatchesConstants';
import { COLOR_DISPLAY, COLOR_SPACE } from '../../../../../common/colors/colorConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';
import { ElementToolSettings, ToolbarItemConfig } from '../../toolbarTypes';
import { ImMNCurrentUser } from '../../../../user/currentUserSelector';

const getDefaultContent = (color: string, userPreferredColorDisplay: string, shouldUseTiptapContent: boolean) => () => {
    const showColorValue = userPreferredColorDisplay !== COLOR_DISPLAY.OFF;

    // Initialise color object in HEX format if user does not have a preferred color display
    const colorSpace = showColorValue ? userPreferredColorDisplay : COLOR_SPACE.HEX;
    const colorObject = parseColorObject(color, colorSpace);
    const colorName = getColorName(color);

    const caption = shouldUseTiptapContent
        ? convertSimpleStringToTiptapContent(colorName || '')
        : rawFromText(colorName);

    return {
        color: colorObject,
        caption,
        showCaption: true,
        showColorValue,
    };
};

const mapStateToProps = createStructuredSelector({
    suggestedColors: currentBoardSuggestedColorsSelector,
});

let colorIndex = 0;

const getSuggestedColorWithIndex = (suggestedColors: string[], index: number): string =>
    suggestedColors[index % suggestedColors.length];

type ColorSwatchToolProps = {
    currentUser: ImMNCurrentUser;
    currentBoardId: string;
    item: ToolbarItemConfig;
    elementTypeObject: any;
    gridSize: number;
    selectOnCreate: boolean;
    editOnCreate: boolean;
    currentUserId: string;
    permissions: number;
    suggestedColors: string[];
};

const ColorSwatchTool = (props: ColorSwatchToolProps) => {
    const { item, currentBoardId, gridSize, currentUser, currentUserId, permissions, suggestedColors } = props;

    const toolSettings = item?.toolSettings as ElementToolSettings | undefined;

    const elementTypeObject = toolSettings?.elementTypeObject || props.elementTypeObject;
    const selectOnCreate = toolSettings?.selectOnCreate ?? props.selectOnCreate;
    const editOnCreate = toolSettings?.editOnCreate ?? props.editOnCreate;

    const userPreferredColorDisplay = getUserPreferredColorDisplay(currentUser);
    const shouldUseTiptapContent = isFeatureEnabled(ExperimentId.tiptapConversion, currentUser);

    const [color, setColor] = useState(getSuggestedColorWithIndex(suggestedColors, colorIndex));

    const refreshColor = () => {
        // Cycle through colors in suggestedColors
        colorIndex += 1;

        const newColor = getSuggestedColorWithIndex(suggestedColors, colorIndex);
        setColor(newColor);
    };

    return (
        <RequireElementTypeEditPermission elementType={elementTypeObject.elementType} permissions={permissions}>
            <ElementTool
                name={COLOR_SWATCH_DISPLAY_NAME}
                toolName={'color-swatch'}
                elementType={elementTypeObject.elementType}
                currentBoardId={currentBoardId}
                currentUserId={currentUserId}
                gridSize={gridSize}
                editOnCreate={editOnCreate}
                selectOnCreate={selectOnCreate}
                getDefaultContent={getDefaultContent(color, userPreferredColorDisplay, shouldUseTiptapContent)}
                onDragEnd={refreshColor}
            />
        </RequireElementTypeEditPermission>
    );
};

export default connect(mapStateToProps)(ColorSwatchTool);
